// Dialog de envio de teste (§4.5): canal, destinatário (validação
// e-mail/telefone conforme o canal), versão e transação de exemplo
// opcional → POST …/test-send. O resultado (testId + warning do
// payload) é exibido no próprio dialog.
import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect, useRef } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { z } from "zod"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import type {
  NotificationSearchChannelType,
  NotificationTemplateVersionType,
} from "@/types/notification-center"

import { NOTIFICATION_SEARCH_CHANNELS } from "../constants"
import { useNotificationCenterTemplateEvents } from "../hooks"
import { useTestSendTemplate } from "../template-editor-mutations"
import { isValidTestRecipient } from "../template-editor-utils"

const testSendPrefix =
  "pages.notificationCenter.templateEditorPage.testSend"

// Valor sentinela para "sample genérico" (Radix Select não aceita
// value=""); mapeado para `event: undefined` no submit.
const GENERIC_EVENT = "__generic__"

// Schema com validação de destinatário DEPENDENTE do canal.
const buildTestSendSchema = (
  invalidEmail: string,
  invalidPhone: string
) =>
  z
    .object({
      channel: z.string(),
      recipient: z.string(),
      version: z.string(),
      sampleTransactionId: z.string().optional(),
      event: z.string(),
    })
    .superRefine((data, context) => {
      if (
        !isValidTestRecipient(data.channel, data.recipient)
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["recipient"],
          message:
            data.channel === "EMAIL"
              ? invalidEmail
              : invalidPhone,
        })
      }
    })

type TemplateTestSendDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  templateUuid: string
  defaultChannel: NotificationSearchChannelType
  defaultVersion: number
  versions: NotificationTemplateVersionType[]
}

export const TemplateTestSendDialog = ({
  open,
  onOpenChange,
  templateUuid,
  defaultChannel,
  defaultVersion,
  versions,
}: TemplateTestSendDialogProps) => {
  const { t } = useTranslation()
  const testSendMutation = useTestSendTemplate(templateUuid)

  const form = useForm({
    resolver: zodResolver(
      buildTestSendSchema(
        t(`${testSendPrefix}.invalidEmail`),
        t(`${testSendPrefix}.invalidPhone`)
      )
    ),
    defaultValues: {
      channel: defaultChannel as string,
      recipient: "",
      version: String(defaultVersion),
      sampleTransactionId: "",
      event: GENERIC_EVENT,
    },
  })

  // Eventos do template (só busca com o dialog aberto). Associados
  // primeiro, para o front pré-marcar o cenário mais provável.
  const { data: eventsData } =
    useNotificationCenterTemplateEvents(templateUuid, {
      enabled: open,
    })
  const events = eventsData?.events ?? []
  const associatedEvents = events.filter(
    (item) => item.associated
  )
  const sortedEvents = [
    ...associatedEvents,
    ...events.filter((item) => !item.associated),
  ]

  // Pré-marca o 1º evento associado UMA vez, quando os eventos
  // chegam e o usuário ainda não mexeu no campo (evita sobrescrever
  // uma escolha manual em refetches).
  const didPreselectEvent = useRef(false)
  useEffect(() => {
    if (
      !didPreselectEvent.current &&
      associatedEvents.length > 0 &&
      form.getValues("event") === GENERIC_EVENT
    ) {
      form.setValue("event", associatedEvents[0].event)
      didPreselectEvent.current = true
    }
  }, [associatedEvents, form])

  const handleSubmit = form.handleSubmit((data) => {
    testSendMutation.mutate({
      channel:
        data.channel as NotificationSearchChannelType,
      recipient: data.recipient,
      version: Number(data.version),
      sampleTransactionId:
        data.sampleTransactionId || undefined,
      event:
        data.event === GENERIC_EVENT
          ? undefined
          : data.event,
    })
  })

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      testSendMutation.reset()
      form.reset()
      didPreselectEvent.current = false
    }
    onOpenChange(nextOpen)
  }

  const result = testSendMutation.data

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {t(`${testSendPrefix}.title`)}
          </DialogTitle>
          <DialogDescription>
            {t(`${testSendPrefix}.description`)}
          </DialogDescription>
        </DialogHeader>
        {result ? (
          <div
            className="space-y-3"
            data-testid="test-send-result"
          >
            <p className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
              {t(`${testSendPrefix}.resultTitle`, {
                recipient: result.recipient,
              })}
            </p>
            <dl className="space-y-1 text-sm">
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">
                  {t(`${testSendPrefix}.resultTestId`)}
                </dt>
                <dd className="break-all font-mono text-xs">
                  {result.testId}
                </dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">
                  {t(`${testSendPrefix}.resultProvider`)}
                </dt>
                <dd>{result.provider}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">
                  {t(`${testSendPrefix}.resultVersion`)}
                </dt>
                <dd className="tabular-nums">
                  v{result.templateVersion}
                </dd>
              </div>
            </dl>
            {result.warning && (
              <div
                data-testid="test-send-warning"
                className="max-h-60 overflow-auto whitespace-pre-wrap break-words rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700"
              >
                {result.warning}
              </div>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
              >
                {t(`${testSendPrefix}.close`)}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <Form {...form}>
            <form
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              <FormField
                control={form.control}
                name="channel"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t(`${testSendPrefix}.channel`)}
                    </FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {NOTIFICATION_SEARCH_CHANNELS.map(
                          (channel) => (
                            <SelectItem
                              key={channel}
                              value={channel}
                            >
                              {channel}
                            </SelectItem>
                          )
                        )}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="recipient"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t(`${testSendPrefix}.recipient`)}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={t(
                          `${testSendPrefix}.recipientPlaceholder`
                        )}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="version"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t(`${testSendPrefix}.version`)}
                    </FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {versions.map((item) => (
                          <SelectItem
                            key={item.version}
                            value={String(item.version)}
                          >
                            v{item.version}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="event"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t(`${testSendPrefix}.event`)}
                    </FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={GENERIC_EVENT}>
                          {t(
                            `${testSendPrefix}.eventGeneric`
                          )}
                        </SelectItem>
                        {sortedEvents.map((item) => (
                          <SelectItem
                            key={item.event}
                            value={item.event}
                          >
                            {item.label}
                            {item.associated
                              ? ` — ${t(
                                  `${testSendPrefix}.eventAssociated`
                                )}`
                              : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="sampleTransactionId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t(
                        `${testSendPrefix}.sampleTransactionId`
                      )}
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                >
                  {t(`${testSendPrefix}.cancel`)}
                </Button>
                <Button
                  type="submit"
                  className="btn-whitelabel"
                  disabled={testSendMutation.isPending}
                >
                  {t(`${testSendPrefix}.submit`)}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  )
}
